<template>
  <div
    class="card-box_content"
    data-tst="current_plan"
    :style="`--clr-type:var(--clr-${type}-back)`"
  >
    <TierTag
      :paymentInterval="plan.interval"
      :customGame="plan.game_format.toUpperCase()"
      :customTier="plan.tier"
      :hasIcon="true"
      :hasSubSlash="false"
    />

    <p
      v-if="la.text.free || la.text.trial || la.text.withPlan || la.text.charge"
      class="card-box_text mt-2"
    >
      <span
        v-if="la.text.free"
        class="card-box_text_item free"
        v-html="la.text.free"
      ></span>
      <span
        v-if="la.text.trial"
        class="card-box_text_item trial"
        v-html="la.text.trial"
      ></span>
      <span
        v-if="la.text.withPlan"
        class="card-box_text_item withPlan"
        v-html="la.text.withPlan"
      ></span>
      <span
        v-if="la.text.charge"
        class="card-box_text_item charge"
        v-html="la.text.charge"
      ></span>
    </p>

    <ul
      v-if="isFree"
      class="mt-2"
    >
      <li>{{ $t(`tier.perks.free.spots_day`, { free_spots_day: 1 }) }}</li>
      <li>{{ $t(`tier.perks.free.hands_day`, { free_hands_day: 10 }) }}</li>
      <li>{{ $t(`tier.perks.free.hands_month`, { free_hands_mo: 5 }) }}</li>
    </ul>

    <div
      v-if="la.text_update"
      class="subscription-info-box"
      data-tst="updated_plan_info"
    >
      <div class="d-flex clr-analyze weight_500">
        <v-icon
          class="tier-tag_icon"
          color="#ffd54f"
          >mdi-alert-circle-outline</v-icon
        >
        {{ la.text_update.title }}
      </div>
      <p
        class="mt-2 mb-2"
        v-html="la.text_update.message"
      ></p>
    </div>

    <v-btn
      v-if="plan.tier === 'TRIAL' || plan.tier === 'FREE'"
      class="ml-auto mt-6"
      :data-tst="`btn_upgrade_sub_${planTestId}`"
      color="primary"
      size="large"
      @click="$router.push('/subscription')"
    >
      <v-icon class="mr-2">mdi-crown</v-icon>
      {{ $t('btn.upgrade') }}</v-btn
    >
    <div
      v-else-if="!sub.canceled && plan.provider !== 'Gtowizard'"
      class="d-flex justify-end ml-auto mt-4 ga-2 flex-wrap"
    >
      <v-btn
        :data-tst="`btn_change_sub_${planTestId}`"
        color="rgba(255, 255, 255, 0.075)"
        @click="openDialog"
        >{{ $t('btn.change') }}</v-btn
      >
      <v-btn
        v-if="sub.updated_plan && plan.provider === 'Adyen'"
        color="primary"
        :data-tst="`btn_reactivate_previous_${planTestId}`"
        :loading="reactivateLoading"
        @click="cancelDowngrade"
      >
        {{ $t('tier.reactivate_prev_plan').toUpperCase() }}</v-btn
      >
    </div>

    <div
      v-if="sub.canceled"
      class="subscription-info-box f-column mt-4"
      data-tst="warn_plan_canceled"
    >
      <div class="d-flex clr-analyze weight_500">
        <v-icon
          class="tier-tag_icon"
          color="#ffd54f"
          >mdi-alert-circle-outline</v-icon
        >
        {{ $t(`tier.plan_no_renew`) }}
      </div>
      <p
        class="mt-2 mb-2"
        v-html="
          $t(`tier.plan_no_renew_message`, {
            date: la.expirationDate,
            game: $t(`gametypes.groups.${plan.game_format.toLowerCase()}_long`),
            plan: $t(`tier.options.${plan.tier}`),
            interval: $t(`tier.options.${plan.interval}`),
          })
        "
      ></p>
      <v-btn
        class="ml-auto mt-4"
        color="primary"
        :data-tst="`btn_reactivate_${planTestId}`"
        :loading="reactivateLoading"
        @click="reactivateSubscription"
      >
        {{ $t(`tier.reactivate_plan`).toUpperCase() }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import { gtm_sub_renewal } from '@/helpers/gtm'

import format_mix from '@/mixins/format_mix'
import TierTag from '@/components/Sub/TierTag'
import { updateSubscription } from '@/helpers/paymentHelpers'

export default {
  name: 'UserSubscriptionBox',
  components: {
    TierTag,
  },
  mixins: [format_mix],
  props: {
    sub: Object,
    hasFreeText: Boolean,
  },
  data() {
    return {
      reactivateLoading: false,
    }
  },
  methods: {
    async reactivateSubscription() {
      if (this.reactivateLoading) return
      this.reactivateLoading = true
      const provider =
        this.sub.plan.provider === 'Paypal'
          ? 'paypal'
          : this.sub.plan.provider === 'Stripe'
            ? 'stripe'
            : 'adyen'
      const res = await this.$store.dispatch('api_call', {
        method: 'POST',
        endpoint:
          provider === 'adyen'
            ? `payments/subscription/adyen/${this.sub.public_id}/reactivate/`
            : `payments/subscription/${provider}/activate/`,
        data: {
          public_id: this.sub.public_id,
        },
      })
      if (res?.response?.status === 204 || res?.response?.status === 200) {
        // const option = this.currentTierPlan
        gtm_sub_renewal({
          currencyCode: 'USD',
          game: this.sub.plan.type,
          type: this.sub.plan.tier.toLowerCase(),
          paymentType: this.sub.plan.interval.toLowerCase(),
          price: this.sub.plan.price,
        })
        await updateSubscription(this.sub.plan, 'reactivate', () => {
          this.$store.dispatch('gameformats_reinitialize_after_tier_update')
          this.reactivateLoading = false
        })
      } else {
        this.$store.dispatch('messages', {
          type: 'error',
          status: 'reactivation_failed',
          statusMessages: {
            _reactivation_failed: {
              i18n: 'payment.reactivation_failed',
            },
          },
        })
        this.reactivateLoading = false
      }
    },
    async cancelDowngrade() {
      if (this.reactivateLoading) return
      this.reactivateLoading = true
      const res = await this.$store.dispatch('api_call', {
        method: 'POST',
        endpoint: `payments/subscription/adyen/${this.sub.public_id}/cancel-downgrade/`,
      })
      if (res?.response?.status === 204 || res?.response?.status === 200) {
        await updateSubscription(this.sub.plan, 'reactivate', () => {
          this.$store.dispatch('gameformats_reinitialize_after_tier_update')
          this.reactivateLoading = false
        })
      } else {
        this.$store.dispatch('messages', {
          type: 'error',
          status: 'reactivation_failed',
          statusMessages: {
            _reactivation_failed: {
              i18n: 'payment.reactivation_failed',
            },
          },
        })
        this.reactivateLoading = false
      }
    },
    openDialog() {
      const { plan } = this.sub
      this.$store.dispatch('router_update_query', {
        query: {
          gm_type: plan.type.toLowerCase(),
          gm_tier: plan.tier.toLowerCase(),
          gm_interval: plan.interval.toLowerCase(),
        },
        push: false,
      })
      setTimeout(() => {
        this.$store.dispatch('router_set_dialogs', 'update-payment-plan-dialog')
      }, 0)
    },
  },
  computed: {
    plan() {
      return this.sub.plan
    },
    type() {
      return this.isTrial ? 'trial' : this.plan?.type?.toLowerCase() || ''
    },
    updated_plan() {
      return this.sub.updated_plan
    },
    isFree() {
      return this.plan.tier === 'FREE'
    },
    isTrial() {
      return this.plan.tier === 'TRIAL'
    },
    user_info() {
      const { user_info } = this.$store.getters
      return user_info
    },
    la() {
      const expDate = this.sub.expiration_date
      const expirationDate = this.formatISODate(expDate)
      const subCanceled = this.sub.canceled
      const subUpdate = this.sub.updated_plan
      const hasSub = this.plan.tier !== 'FREE'
      const trial = this.plan.tier === 'TRIAL'
      const { provider } = this.plan

      const allPrices = this.plan.prices.reduce((a, b) => [...a, Number(b.price)], [])
      const renewPrice = Math.min(...allPrices)

      const allUpdatePrices = (this.updated_plan?.prices || []).reduce(
        (a, b) => [...a, Number(b.price)],
        [],
      )
      const updatePrice = Math.min(...allUpdatePrices)

      const res = {
        expirationDate,
        text: {
          free: hasSub ? '' : this.hasFreeText ? this.$t(`user_settings.using_free_version`) : '',
          trial: trial
            ? this.$t('user_settings.trial_will_expire_on_date', {
                date: expirationDate,
              })
            : '',
          withPlan:
            !trial && hasSub
              ? this.$t(
                  `user_settings.${
                    subCanceled
                      ? 'sub_will_expire_on_date'
                      : provider === 'Gtowizard' || provider === 'Confirmo'
                        ? 'sub_will_expire_on_date'
                        : 'sub_will_renew_on_date'
                  }`,
                  {
                    date: expirationDate,
                  },
                )
              : '',
          charge: !(
            subCanceled ||
            provider === 'Gtowizard' ||
            provider === 'Confirmo' ||
            trial ||
            !hasSub
          )
            ? this.$t('user_settings.sub_will_charge_via_tra01', {
                amount: renewPrice,
                amount_month:
                  this.plan.interval === 'YEARLY'
                    ? `(${this.numFixed(renewPrice / 12)} USD / ${this.$t(`common.month`)})`
                    : '',
                payment_method: provider === 'Paypal' ? 'Paypal' : this.$t('payment.credit_card'),
              })
            : '',
        },

        text_update: subUpdate
          ? {
              title: this.$t(`tier.plan_updated`),
              message: `${this.$t(`tier.plan_will_change`, {
                plan: this.$t(`tier.options.${this.plan.tier}`),
                game: this.$t(`gametypes.groups.${this.plan.game_format.toLowerCase()}_long`),
                interval: this.$t(`tier.options.${this.plan.interval}`),
                new_plan: this.$t(`tier.options.${this.updated_plan.tier}`),
                new_game: this.$t(`gametypes.groups.${this.updated_plan.type.toLowerCase()}_long`),
                new_interval: this.$t(`tier.options.${this.updated_plan.interval}`),
                date: this.formatISODate(this.sub.updated_plan_date),
              })} <br/>
            ${this.$t(`tier.next_charge_tra01`, {
              amount: this.numFixed(updatePrice),
              timespan:
                this.updated_plan.interval === 'YEARLY'
                  ? this.$t(`common.year`)
                  : this.$t(`common.month`),
            })}`,
            }
          : null,
      }
      // console.log(res)
      return res
    },
    planTestId() {
      return `${this.plan.game_format.toLowerCase()}_${this.plan.tier.toLowerCase()}_${this.plan.interval.toLowerCase()}`
    },
  },
}
</script>
<style lang="scss" scoped>
@media (max-width: 1023px) {
  .card-box {
    flex-direction: column;
    &_title {
      margin-bottom: 1rem;
    }
    &_content {
      max-width: calc(100vw - 2.5rem) !important;
    }
  }
}
.user-subscription {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-box {
  &_content {
    // border-radius: 4px;
    // box-shadow: 0px 0px 0px 1px var(--clr-black-3);
    // padding: 1rem;
    --clr-type: red;
    // --clr-transparent:transparent
    --clr-back: linear-gradient(225deg, var(--clr-type), transparent);
    display: flex;
    flex-direction: column;
    max-width: 28rem;
    padding: 1rem;
    border-radius: 8px;
    background: var(--clr-back);
    // max-width: 32rem;
    flex-shrink: 0;
  }
  &_text {
    position: relative;
    border-radius: 4px;
    &_item {
      &:first-child {
        padding-left: 0;
      }
      padding-left: 4px;
    }
  }
}

.tier-tag {
  border-radius: 4px;
  font-weight: 700;
  font-size: 1.25rem;
  flex: 0;
  // &_trial {
  //   color: var(--clr-white-0);
  // }
  // &_starter {
  //   color: var(--clr-blue);
  // }
  // &_premium {
  //   color: var(--clr-yellow);
  // }
  &_icon {
    font-size: 1.5rem;
    margin: 0 0.5rem 0 0;
    // color: currentColor;
  }
}
</style>
